import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { DesignSystem, getSpacing } from '../styles/designSystem';
import { TourismAPI, EnquiryData } from '../api/tourism';
import { useSession } from '../contexts/SessionContext';

// ─── Props ────────────────────────────────────────────────────────────

interface EnquiryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  sourcePage: string;
  itineraryId?: string;
  itineraryTitle?: string;
}

// ─── Animations ───────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; backdrop-filter: blur(0px); }
  to   { opacity: 1; backdrop-filter: blur(12px); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(40px) scale(0.95); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

// ─── Styled Components ───────────────────────────────────────────────

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(12px);
  z-index: ${DesignSystem.zIndex.modal + 20};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${getSpacing('lg')};
  animation: ${fadeIn} 0.3s ease-out;
  opacity: ${p => p.$isOpen ? 1 : 0};
  pointer-events: ${p => p.$isOpen ? 'all' : 'none'};
  transition: opacity 0.3s ease;
`;

const ModalContainer = styled.div`
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 25px 80px rgba(0,0,0,0.35), 0 0 0 1px rgba(202,185,110,0.1);
  max-width: 520px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  font-family: 'DM Sans', sans-serif;
  animation: ${slideUp} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);

  @media (max-width: 600px) {
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid rgba(255,255,255,0.3);
  background: rgba(255,255,255,0.15);
  color: #ffffff;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  transition: all 0.2s ease;
  backdrop-filter: blur(4px);

  &:hover {
    background: rgba(255,255,255,0.3);
    border-color: rgba(255,255,255,0.5);
    transform: scale(1.05);
  }
`;

const PromoHeader = styled.div`
  background: linear-gradient(135deg, #CAB96E 0%, #b8a75e 50%, #CAB96E 100%);
  padding: 44px 32px 32px;
  position: relative;
  border-radius: 20px 20px 0 0;
  text-align: center;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: radial-gradient(ellipse at 30% 0%, rgba(255,255,255,0.15) 0%, transparent 60%);
    pointer-events: none;
  }

  &::after {
    content: '◆  ◆  ◆';
    display: block;
    margin-top: 16px;
    font-size: 0.35rem;
    color: rgba(255,255,255,0.5);
    letter-spacing: 0.3em;
  }

  @media (max-width: 600px) {
    padding: 36px 20px 28px;
    border-radius: 0;
  }
`;

const PromoTitle = styled.h2`
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1.4;
  margin: 0;
  color: #ffffff;
  font-family: 'Poppins', 'Montserrat', 'DM Sans', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  position: relative;
  text-shadow: 0 1px 2px rgba(0,0,0,0.08);
`;

const FormBody = styled.div`
  padding: 32px 32px 28px;

  @media (max-width: 600px) {
    padding: 24px 20px 20px;
  }
`;

const FormTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 500;
  color: #CAB96E;
  margin: 0 0 24px 0;
  font-family: 'Poppins', 'Montserrat', 'DM Sans', sans-serif;
  font-style: italic;
  letter-spacing: -0.01em;
`;

const FormRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 14px;
  align-items: stretch;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const InputWrapper = styled.div<{ flex?: number }>`
  flex: ${p => p.flex ?? 1};
  display: flex;
  flex-direction: column;
`;

const Input = styled.input`
  width: 100%;
  height: 46px;
  padding: 0 16px;
  border: 1px solid #e8e4dc;
  border-radius: 10px;
  font-size: 0.9rem;
  font-family: ${DesignSystem.typography.fonts.primary};
  color: #1a1a1a;
  outline: none;
  transition: all 0.2s ease;
  box-sizing: border-box;
  background: #fafaf8;

  /* Hide number spinners */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type=number] {
    -moz-appearance: textfield;
  }

  &::placeholder { color: #aaa; }
  &:focus {
    border-color: #CAB96E;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(202,185,110,0.1);
  }
`;

const Select = styled.select`
  height: 46px;
  padding: 0 10px;
  border: 1px solid #e8e4dc;
  border-radius: 10px;
  font-size: 0.9rem;
  font-family: ${DesignSystem.typography.fonts.primary};
  color: #1a1a1a;
  outline: none;
  background: #fafaf8;
  cursor: pointer;
  min-width: 80px;
  box-sizing: border-box;
  transition: all 0.2s ease;

  &:focus {
    border-color: #CAB96E;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(202,185,110,0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #e8e4dc;
  border-radius: 10px;
  font-size: 0.9rem;
  font-family: ${DesignSystem.typography.fonts.primary};
  color: #1a1a1a;
  outline: none;
  resize: vertical;
  min-height: 80px;
  transition: all 0.2s ease;
  box-sizing: border-box;
  background: #fafaf8;

  &::placeholder { color: #aaa; }
  &:focus {
    border-color: #CAB96E;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(202,185,110,0.1);
  }
`;

const DateInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const DateInput = styled.input<{ $hasValue: boolean }>`
  width: 100%;
  height: 46px;
  padding: 0 16px;
  border: 1px solid #e8e4dc;
  border-radius: 10px;
  font-size: 0.9rem;
  font-family: ${DesignSystem.typography.fonts.primary};
  color: ${p => p.$hasValue ? '#1a1a1a' : 'transparent'};
  outline: none;
  transition: all 0.2s ease;
  box-sizing: border-box;
  background: #fafaf8;
  cursor: pointer;

  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.5;
    transition: opacity 0.2s ease;
    &:hover { opacity: 1; }
  }

  &:focus {
    border-color: #CAB96E;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(202,185,110,0.1);
    color: #1a1a1a;
  }

  &:focus + span { display: none; }
`;

const DatePlaceholder = styled.span`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #aaa;
  font-size: 0.9rem;
  font-family: ${DesignSystem.typography.fonts.primary};
  pointer-events: none;
`;

const PrivacyRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 6px;
  margin-bottom: 6px;
`;

const PrivacyCheck = styled.span`
  color: #CAB96E;
  font-size: 1rem;
  flex-shrink: 0;
  margin-top: 1px;
`;

const PrivacyText = styled.span`
  font-size: 0.8rem;
  color: #666;
  line-height: 1.4;
  font-family: 'DM Sans', sans-serif;
`;

const SubmitRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  margin-top: 4px;
`;

const SubmitButton = styled.button<{ $loading?: boolean }>`
  padding: 13px 32px;
  background: #CAB96E;
  color: #ffffff;
  border: 1px solid #CAB96E;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  cursor: ${p => p.$loading ? 'wait' : 'pointer'};
  opacity: ${p => p.$loading ? 0.7 : 1};
  transition: all 0.3s ease;
  font-family: 'DM Sans', sans-serif;
  box-shadow: 0 4px 16px rgba(202,185,110,0.25);

  &:hover:not(:disabled) {
    background: #b8a75e;
    border-color: #b8a75e;
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 6px 20px rgba(202,185,110,0.35);
  }
  &:active:not(:disabled) { transform: translateY(0) scale(0.98); }
  &:disabled { cursor: not-allowed; opacity: 0.6; }
`;

const ErrorText = styled.p`
  color: #d32f2f;
  font-size: 0.8rem;
  margin: 4px 0 0;
  font-family: 'DM Sans', sans-serif;
`;

const SuccessContainer = styled.div`
  text-align: center;
  padding: 60px 32px;
`;

const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #CAB96E, #b8a75e);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin: 0 auto 20px;
  box-shadow: 0 4px 20px rgba(202,185,110,0.3);
`;

const SuccessTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 500;
  color: #CAB96E;
  margin: 0 0 10px;
  font-family: 'Poppins', 'Montserrat', 'DM Sans', sans-serif;
  font-style: italic;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SuccessText = styled.p`
  font-size: 0.95rem;
  color: #666;
  margin: 0;
  line-height: 1.5;
  font-family: 'DM Sans', sans-serif;
`;

// ─── Country codes ────────────────────────────────────────────────────

const COUNTRY_CODES = [
  { code: '+91', label: '🇮🇳 +91' },
  { code: '+1',  label: '🇺🇸 +1' },
  { code: '+44', label: '🇬🇧 +44' },
  { code: '+61', label: '🇦🇺 +61' },
  { code: '+971', label: '🇦🇪 +971' },
  { code: '+65', label: '🇸🇬 +65' },
  { code: '+49', label: '🇩🇪 +49' },
  { code: '+33', label: '🇫🇷 +33' },
  { code: '+81', label: '🇯🇵 +81' },
  { code: '+86', label: '🇨🇳 +86' },
  { code: '+82', label: '🇰🇷 +82' },
  { code: '+7',  label: '🇷🇺 +7' },
  { code: '+39', label: '🇮🇹 +39' },
  { code: '+55', label: '🇧🇷 +55' },
  { code: '+27', label: '🇿🇦 +27' },
];

// ─── Component ────────────────────────────────────────────────────────

const EnquiryDialog: React.FC<EnquiryDialogProps> = ({
  isOpen,
  onClose,
  sourcePage,
  itineraryId,
  itineraryTitle,
}) => {
  const { sessionId } = useSession();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [dateOfTravel, setDateOfTravel] = useState('');
  const [travellerCount, setTravellerCount] = useState('');
  const [message, setMessage] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setName('');
      setEmail('');
      setPhone('');
      setCountryCode('+91');
      setDateOfTravel('');
      setTravellerCount('');
      setMessage('');
      setSubmitting(false);
      setSuccess(false);
      setError('');
    }
  }, [isOpen]);

  // Auto-close after success
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => onClose(), 2500);
      return () => clearTimeout(timer);
    }
  }, [success, onClose]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleSubmit = useCallback(async () => {
    setError('');

    // Basic validation
    if (!name.trim()) { setError('Please enter your name.'); return; }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError('Please enter a valid email.'); return; }
    if (!phone.trim() || phone.trim().length < 4) { setError('Please enter a valid phone number.'); return; }

    setSubmitting(true);
    try {
      const payload: EnquiryData = {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        country_code: countryCode,
        source_page: sourcePage,
        session_id: sessionId || undefined,
      };
      if (dateOfTravel) payload.date_of_travel = dateOfTravel;
      if (travellerCount) payload.traveller_count = parseInt(travellerCount, 10);
      if (message.trim()) payload.message = message.trim();
      if (itineraryId) payload.itinerary_id = itineraryId;
      if (itineraryTitle) payload.itinerary_title = itineraryTitle;

      await TourismAPI.submitEnquiry(payload);
      setSuccess(true);
    } catch (err) {
      console.error('Enquiry submission error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [name, email, phone, countryCode, dateOfTravel, travellerCount, message, sourcePage, itineraryId, itineraryTitle, sessionId]);

  if (!isOpen) return null;

  return (
    <Overlay $isOpen={isOpen} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose} aria-label="Close">✕</CloseButton>

        {success ? (
          <SuccessContainer>
            <SuccessIcon>✓</SuccessIcon>
            <SuccessTitle>Thank You!</SuccessTitle>
            <SuccessText>
              We've received your details. Our travel expert will contact you shortly.
            </SuccessText>
          </SuccessContainer>
        ) : (
          <>
            {/* Earthy gradient header */}
            <PromoHeader>
              <PromoTitle>
                Let Us Craft Your Perfect Himalayan Journey
              </PromoTitle>
            </PromoHeader>

            {/* Form */}
            <FormBody>
              <FormTitle>
                {itineraryTitle
                  ? `Enquire about: ${itineraryTitle}`
                  : 'Tell us about your dream Himalayan trip'}
              </FormTitle>

              <FormRow>
                <InputWrapper>
                  <Input placeholder="Your Name" value={name} onChange={e => setName(e.target.value)} />
                </InputWrapper>
                <InputWrapper>
                  <Input type="email" placeholder="Your Email" value={email} onChange={e => setEmail(e.target.value)} />
                </InputWrapper>
              </FormRow>

              <FormRow>
                <InputWrapper flex={0}>
                  <Select value={countryCode} onChange={e => setCountryCode(e.target.value)}>
                    {COUNTRY_CODES.map(c => (
                      <option key={c.code} value={c.code}>{c.label}</option>
                    ))}
                  </Select>
                </InputWrapper>
                <InputWrapper flex={2}>
                  <Input type="tel" placeholder="Phone Number (10 digits)" value={phone} maxLength={10} onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 10); setPhone(v); }} style={{ minWidth: 200, letterSpacing: '0.05em' }} />
                </InputWrapper>
              </FormRow>

              <FormRow>
                <InputWrapper>
                  <DateInputWrapper>
                    <DateInput type="date" value={dateOfTravel} min={new Date().toISOString().split('T')[0]} onChange={e => setDateOfTravel(e.target.value)} $hasValue={!!dateOfTravel} />
                    {!dateOfTravel && <DatePlaceholder>Date of Travel</DatePlaceholder>}
                  </DateInputWrapper>
                </InputWrapper>
                <InputWrapper>
                  <Input
                    type="number"
                    placeholder="No. of Travellers"
                    min={1}
                    max={100}
                    value={travellerCount}
                    onChange={e => setTravellerCount(e.target.value)}
                  />
                </InputWrapper>
              </FormRow>

              <FormRow>
                <InputWrapper>
                  <TextArea placeholder="Message (optional)" value={message} onChange={e => setMessage(e.target.value)} />
                </InputWrapper>
              </FormRow>

              {error && <ErrorText>{error}</ErrorText>}

              <PrivacyRow>
                <PrivacyCheck>✓</PrivacyCheck>
                <PrivacyText>Your details are safe with us - used only to contact you.</PrivacyText>
              </PrivacyRow>

              <SubmitRow>
                <SubmitButton onClick={handleSubmit} disabled={submitting} $loading={submitting}>
                  {submitting ? 'Sending...' : 'GET CALLBACK'}
                </SubmitButton>
              </SubmitRow>
            </FormBody>
          </>
        )}
      </ModalContainer>
    </Overlay>
  );
};

export default EnquiryDialog;
